import { useEffect, useMemo, useState } from 'react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
}

export function TableOfContents({ content }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('');

  // Extract headings from markdown content
  const headings = useMemo(() => {
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const matches: Heading[] = [];
    let match;

    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      // Create slug from heading text
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fff\s-]/g, '') // Keep Chinese characters
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      matches.push({ id, text, level });
    }

    return matches;
  }, [content]);

  // Track active heading based on scroll position
  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-80px 0px -80% 0px',
      }
    );

    // Observe all heading elements
    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [headings]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  // Find minimum heading level to normalize indentation
  const minLevel = headings.length > 0 ? Math.min(...headings.map((h) => h.level)) : 1;

  return (
    <div className="fixed top-24 right-8 w-64 hidden xl:block z-30">
      <div className="toc-card rounded-xl shadow-lg border border-gray-200 overflow-hidden bg-white">
        {/* Header */}
        <div className="toc-header px-4 py-3 border-b border-gray-200">
          <span className="toc-title text-sm font-semibold text-gray-900">
            Index
          </span>
        </div>

        {/* Content */}
        <nav className="max-h-[60vh] overflow-y-auto p-3">
          {headings.length === 0 ? (
            <p className="toc-empty text-sm text-gray-500 px-2 py-1">
              Index is empty
            </p>
          ) : (
            <ul className="space-y-1">
              {headings.map((heading, index) => {
                const indent = (heading.level - minLevel) * 12;
                const isActive = activeId === heading.id;

                return (
                  <li key={`${heading.id}-${index}`}>
                    <button
                      onClick={() => handleClick(heading.id)}
                      className={`toc-item w-full text-left text-sm py-1.5 px-2 rounded-md transition-colors truncate ${
                        isActive ? 'active font-medium' : ''
                      }`}
                      style={{ paddingLeft: `${8 + indent}px` }}
                      title={heading.text}
                    >
                      {heading.text}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>
      </div>
    </div>
  );
}
